import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { qk } from "@/api/queryKeys";
import { useApiMutation } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { saveCustomView as saveCustomViewUrl } from "@/service/reqUrl.service";

import type { CustomViewDetails, SaveCustomViewParams } from "./types";

interface UseSaveCustomViewResult {
  /** Persist a custom view via POST /dashboard/save-custom-view. */
  saveCustomView: (
    perfName: string,
    details: CustomViewDetails,
    dashboardID?: number | string | null,
  ) => Promise<boolean>;
}

interface SaveVariables {
  perfName: string;
  details: CustomViewDetails;
  dashboardID?: number | string | null;
}

/**
 * Calls `POST /dashboard/save-custom-view` (`[DayLight].[SaveAppUserPerfs]`).
 * Fired when the user saves a By-Branch or By-Clinician column layout.
 *
 * On success it invalidates the saved-preferences entry for the dashboard that
 * was written to, so the views picker refreshes itself. The existing
 * `.then(ok => ok && refetchViews())` at the call sites still works and is now
 * redundant — TanStack de-duplicates the concurrent refetch — so it can be
 * removed at leisure rather than as part of this change.
 *
 * No optimistic update: the SP returns a server-generated `AppUserPerfID` that
 * the delete call needs, and saving a view is not latency-sensitive.
 */
export function useSaveCustomView(): UseSaveCustomViewResult {
  const queryClient = useQueryClient();
  const userKey = useUserKey();

  const { run } = useApiMutation<SaveVariables>({
    url: saveCustomViewUrl,
    fallbackMessage: "Failed to save view",
    buildBody: ({ perfName, details, dashboardID }) => {
      const params: SaveCustomViewParams = { perfName, perfDetails: details };
      // Omitted entirely rather than sent as null — All Metrics' own views are
      // saved with `@DashboardID = NULL`, which is what an absent field means.
      if (dashboardID != null && dashboardID !== "") {
        params.dashboardID = dashboardID;
      }
      return params;
    },
    onSuccess: ({ dashboardID }) => {
      queryClient.invalidateQueries({
        queryKey: qk.perfs(userKey, dashboardID ?? null),
      });
    },
  });

  const saveCustomView = useCallback(
    (
      perfName: string,
      details: CustomViewDetails,
      dashboardID?: number | string | null,
    ): Promise<boolean> => run({ perfName, details, dashboardID }),
    [run],
  );

  return { saveCustomView };
}
